import type { QueryClient, QueryKey } from "@tanstack/react-query";

/** React Query root for `DeliveriesTable` (`getAllFiltered` list). Must match `DeliveriesTable` query keys. */
export const deliveriesTableQueryRoot = ["deliveries-table"] as const;

export type DeliveriesTableQueryData = {
  records: Array<{ id: number }>;
  total: number;
};

export type DeliveriesTableQuerySnapshot = Array<[QueryKey, DeliveriesTableQueryData | undefined]>;

export async function cancelDeliveriesTableQueries(queryClient: QueryClient) {
  await queryClient.cancelQueries({ queryKey: deliveriesTableQueryRoot });
}

export function snapshotDeliveriesTableQueries(
  queryClient: QueryClient
): DeliveriesTableQuerySnapshot {
  return queryClient.getQueriesData<DeliveriesTableQueryData>({
    queryKey: deliveriesTableQueryRoot,
  });
}

export function removeDeliveriesFromDeliveriesTableCache(queryClient: QueryClient, ids: number[]) {
  if (ids.length === 0) {
    return;
  }
  const idSet = new Set(ids);
  queryClient.setQueriesData<DeliveriesTableQueryData>(
    { queryKey: deliveriesTableQueryRoot },
    (previous) => {
      if (!previous) {
        return previous;
      }
      const records = previous.records.filter((row) => !idSet.has(row.id));
      if (records.length === previous.records.length) {
        return previous;
      }
      const removed = previous.records.length - records.length;
      return {
        ...previous,
        records,
        total: Math.max(0, previous.total - removed),
      };
    }
  );
}

export function restoreDeliveriesTableQueries(
  queryClient: QueryClient,
  snapshot: DeliveriesTableQuerySnapshot
) {
  for (const [key, data] of snapshot) {
    if (data === undefined) {
      queryClient.removeQueries({ queryKey: key, exact: true });
    } else {
      queryClient.setQueryData(key, data);
    }
  }
}
